import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import VoiceSavedBanner from "@/components/VoiceSavedBanner";
import {
  ClipboardCheck,
  Users,
  UserRound,
  AlertTriangle,
  TrendingUp,
  Loader2,
  ArrowRight,
  Pill,
  Plus,
  Pencil,
  Trash2,
  Clock,
  MapPin,
  Timer,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="mt-2 font-heading text-3xl font-semibold text-clinical-text">
            {value}
          </div>
        </div>
        <span
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: `${accent}1a`, color: accent }}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

const COMPLIANCE_TYPE_LABEL = {
  background_check: "Background check",
  vulnerable_sector_check: "Vulnerable sector",
  criminal_record: "Criminal record",
  work_eligibility: "Work eligibility",
  education_verification: "Education",
  references: "References",
  tb_test: "TB test",
  immunizations: "Immunizations",
  vaccinations: "Vaccinations",
  cpr: "CPR",
  first_aid: "First Aid",
  nursing_license: "Nursing license",
  professional_license: "License",
  drivers_license: "Driver's license",
  insurance: "Insurance",
  other: "Other",
};

function ComplianceWidget({ data }) {
  if (!data) return null;
  const counters = data.counters || {};
  const flagged = data.flagged || [];
  const total = data.total_caregivers || 0;
  const verified = data.fully_verified_caregivers || 0;
  const percent = total > 0 ? Math.round((verified / total) * 100) : 0;
  const expired = counters.expired || 0;
  const expiringSoon = counters.expiring_soon || 0;
  const rejected = counters.rejected || 0;
  const totalAttention = expired + expiringSoon + rejected;

  const statusPill = (status) => {
    if (status === "expired") return { cls: "bg-destructive/10 text-destructive", label: "Expired" };
    if (status === "rejected") return { cls: "bg-destructive/10 text-destructive", label: "Rejected" };
    return { cls: "bg-amber-100 text-amber-800", label: "Expiring soon" };
  };

  return (
    <div className="card-surface p-5" data-testid="compliance-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-clinical-primary" />
          <h2 className="font-heading text-lg font-medium">Caregiver compliance</h2>
        </div>
        <Link
          to="/admin/team"
          className="text-xs text-clinical-primary hover:underline inline-flex items-center gap-1"
          data-testid="compliance-view-team-link"
        >
          Manage in Team <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {/* Top row: caregiver readiness + counter pills */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div
          className="rounded-xl border border-clinical-border bg-clinical-bg p-4 md:col-span-1"
          data-testid="compliance-ready-stat"
        >
          <div className="label-eyebrow">Fully verified</div>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="font-heading text-2xl font-semibold text-clinical-text">
              {verified}
            </span>
            <span className="text-sm text-clinical-subtext">/ {total}</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
            <div
              className="h-full rounded-full bg-clinical-primary"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="mt-1.5 text-[11px] text-clinical-subtext">
            {percent}% audit-ready
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            const el = document.querySelector("[data-testid='compliance-flagged-list']");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-left transition-colors hover:bg-destructive/10"
          data-testid="compliance-expired-stat"
        >
          <div className="flex items-center gap-1.5">
            <ShieldX className="h-3.5 w-3.5 text-destructive" />
            <div className="label-eyebrow text-destructive">Expired</div>
          </div>
          <div className="mt-1 font-heading text-2xl font-semibold text-destructive">
            {expired}
          </div>
          <div className="text-[11px] text-clinical-subtext mt-0.5">
            Block from new shifts
          </div>
        </button>

        <button
          type="button"
          onClick={() => {
            const el = document.querySelector("[data-testid='compliance-flagged-list']");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="rounded-xl border border-amber-300/40 bg-amber-50 p-4 text-left transition-colors hover:bg-amber-100"
          data-testid="compliance-expiring-stat"
        >
          <div className="flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5 text-amber-700" />
            <div className="label-eyebrow text-amber-800">Expiring ≤ 30d</div>
          </div>
          <div className="mt-1 font-heading text-2xl font-semibold text-amber-800">
            {expiringSoon}
          </div>
          <div className="text-[11px] text-clinical-subtext mt-0.5">
            Renew before they lapse
          </div>
        </button>

        <div
          className="rounded-xl border border-clinical-border bg-white p-4"
          data-testid="compliance-pending-stat"
        >
          <div className="label-eyebrow">Pending review</div>
          <div className="mt-1 font-heading text-2xl font-semibold text-clinical-text">
            {(counters.submitted || 0) + (counters.pending || 0)}
          </div>
          <div className="text-[11px] text-clinical-subtext mt-0.5">
            {counters.submitted || 0} submitted · {counters.pending || 0} not started
          </div>
        </div>
      </div>

      {/* Flagged list */}
      {totalAttention === 0 ? (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2"
          data-testid="compliance-empty"
        >
          <ShieldCheck className="h-4 w-4 shrink-0" />
          All caregivers are compliant — nothing expiring in the next 30 days.
        </div>
      ) : (
        <div data-testid="compliance-flagged-list">
          <div className="label-eyebrow mb-2">
            Needs attention ({Math.min(flagged.length, 8)} of {flagged.length})
          </div>
          <ul className="divide-y divide-clinical-border">
            {flagged.slice(0, 8).map((f) => {
              const pill = statusPill(f.status);
              const label = COMPLIANCE_TYPE_LABEL[f.type] || f.label || "Compliance item";
              const dayText =
                f.status === "expired"
                  ? f.expires_on
                    ? `Expired ${f.expires_on}`
                    : "Expired"
                  : f.status === "rejected"
                  ? "Rejected — re-upload needed"
                  : f.days_left != null && f.days_left >= 0
                  ? `${f.days_left} day${f.days_left === 1 ? "" : "s"} left`
                  : "Expiring soon";
              return (
                <li
                  key={`${f.user_id}-${f.item_id}`}
                  className="py-2.5 flex items-start gap-3"
                  data-testid={`compliance-flagged-${f.user_id}-${f.item_id}`}
                >
                  <span className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg ${pill.cls}`}>
                    <ShieldAlert className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-clinical-text truncate">
                      {f.name}
                      <span className="font-normal text-clinical-subtext"> · {label}</span>
                    </div>
                    <div className="text-[11px] text-clinical-subtext mt-0.5">
                      {dayText}
                    </div>
                  </div>
                  <Link
                    to={`/admin/team?user=${f.user_id}`}
                    className="pill bg-clinical-bg text-clinical-text hover:bg-clinical-border/60 transition-colors shrink-0"
                    data-testid={`compliance-resolve-${f.user_id}-${f.item_id}`}
                  >
                    Review
                  </Link>
                </li>
              );
            })}
          </ul>
          {flagged.length > 8 && (
            <div className="mt-3 text-center">
              <Link
                to="/admin/team"
                className="text-xs text-clinical-primary hover:underline inline-flex items-center gap-1"
                data-testid="compliance-view-all-link"
              >
                See all {flagged.length} in Team <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [medChanges, setMedChanges] = useState([]);
  const [activeShifts, setActiveShifts] = useState([]);
  const [hoursSummary, setHoursSummary] = useState([]);
  const [lateArrivals, setLateArrivals] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [riskDue, setRiskDue] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLate = () =>
    api
      .get("/admin/late-arrivals", { params: { hours: 12 } })
      .then(({ data }) => setLateArrivals(data || []))
      .catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/reports", { params: { limit: 6 } }),
      api.get("/admin/alerts"),
      api.get("/admin/recent-med-changes", { params: { hours: 24 } }),
      api.get("/admin/active-shifts"),
      api.get("/admin/hours-summary", { params: { days: 7 } }),
      api.get("/admin/late-arrivals", { params: { hours: 12 } }),
      api.get("/admin/compliance-summary"),
      api.get("/admin/risk-assessments/past-due", { params: { days_buffer: 14 } }),
    ])
      .then(([s, r, a, m, sh, hs, la, cs, rd]) => {
        setStats(s.data);
        setRecent(r.data);
        setAlerts(a.data.alerts || []);
        setMedChanges(m.data.changes || []);
        setActiveShifts(sh.data || []);
        setHoursSummary(hs.data.summary || []);
        setLateArrivals(la.data || []);
        setCompliance(cs.data);
        setRiskDue(rd.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const ackLate = async (id) => {
    try {
      await api.post(`/admin/late-arrivals/${id}/ack`);
      await loadLate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not acknowledge — please retry");
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading dashboard…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Operations</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            Today at a glance
          </h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Live view of caregiver documentation and missed-visit alerts.
          </p>
        </div>
      </div>

      <VoiceSavedBanner />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Reports today"
          value={stats.today_reports}
          icon={ClipboardCheck}
          accent="#004AE6"
          testid="stat-today"
        />
        <StatCard
          label="This week"
          value={stats.week_reports}
          icon={TrendingUp}
          accent="#00B3FF"
          testid="stat-week"
        />
        <StatCard
          label="Active clients"
          value={stats.total_clients}
          icon={Users}
          accent="#FF46A3"
          testid="stat-clients"
        />
        <StatCard
          label="Caregivers"
          value={stats.total_caregivers}
          icon={UserRound}
          accent="#F59E0B"
          testid="stat-caregivers"
        />
      </div>

      <ComplianceWidget data={compliance} />

      {/* Late arrivals (caregiver pings) */}
      {lateArrivals.length > 0 && (
        <div className="card-surface p-5" data-testid="late-arrivals-widget">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600" />
              <h2 className="font-heading text-lg font-medium">Running late</h2>
            </div>
            <span className="label-eyebrow" data-testid="late-arrivals-count">
              {lateArrivals.filter((l) => !l.acknowledged).length} new
            </span>
          </div>
          <ul className="divide-y divide-clinical-border" data-testid="late-arrivals-list">
            {lateArrivals.slice(0, 8).map((l) => {
              const t = l.created_at ? new Date(l.created_at) : null;
              return (
                <li
                  key={l.id}
                  className={`flex items-center justify-between gap-3 py-2.5 ${
                    l.acknowledged ? "opacity-60" : ""
                  }`}
                  data-testid={`late-arrival-${l.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-clinical-text truncate">
                      {l.caregiver_name}
                      <span className="font-normal text-clinical-subtext"> · {l.client_name}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-clinical-subtext">
                      {t ? t.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                      {l.eta_minutes != null && (
                        <> · ETA <span className="font-medium text-clinical-text">{l.eta_minutes} min</span></>
                      )}
                      {l.note && <> · {l.note}</>}
                    </div>
                  </div>
                  {!l.acknowledged ? (
                    <button
                      type="button"
                      onClick={() => ackLate(l.id)}
                      className="btn-secondary"
                      data-testid={`ack-late-${l.id}`}
                    >
                      Acknowledge
                    </button>
                  ) : (
                    <span className="text-xs text-clinical-subtext italic">
                      ✓ {l.acknowledged_by}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Risk assessments past due — clients whose review_due_on has passed */}
      {riskDue.length > 0 && (
        <div className="card-surface p-5" data-testid="risk-due-widget">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              <h2 className="font-heading text-lg font-medium">Risk assessment reviews</h2>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                {riskDue.filter((r) => r.days_overdue >= 0).length} past due
              </span>
            </div>
            <Link
              to="/admin/clients"
              className="text-xs font-medium text-clinical-primary hover:underline"
              data-testid="risk-due-view-all"
            >
              View all clients →
            </Link>
          </div>
          <ul className="space-y-2" data-testid="risk-due-list">
            {riskDue.slice(0, 6).map((r) => {
              const overdue = r.days_overdue >= 0;
              return (
                <li key={r.client_id} className="flex items-center justify-between gap-3 rounded-lg border border-clinical-border bg-white p-2.5"
                  data-testid={`risk-due-row-${r.client_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-clinical-text truncate">{r.client_name}</span>
                      {r.overall_risk && (
                        <span className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase"
                          style={{
                            background: r.overall_risk === "high" ? "#FEE2E2"
                                      : r.overall_risk === "moderate" ? "#FEF3C7" : "#DCFCE7",
                            color: r.overall_risk === "high" ? "#B91C1C"
                                  : r.overall_risk === "moderate" ? "#92400E" : "#166534",
                          }}>
                          {r.overall_risk}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-clinical-subtext">
                      Due {r.review_due_on}
                      {overdue && r.days_overdue > 0 && (
                        <span className="ml-2 font-semibold text-red-700">
                          · {r.days_overdue} day{r.days_overdue === 1 ? "" : "s"} overdue
                        </span>
                      )}
                      {!overdue && (
                        <span className="ml-2 text-amber-700">
                          · in {-r.days_overdue} day{-r.days_overdue === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/admin/clients?client=${r.client_id}&tab=risk`}
                    className="btn-secondary !py-1.5 !text-xs whitespace-nowrap"
                    data-testid={`risk-due-review-${r.client_id}`}
                  >
                    Review <ArrowRight className="h-3 w-3" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Active shifts (live EVV) */}
      <div className="card-surface p-5" data-testid="active-shifts-widget">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-lg font-medium">Active shifts</h2>
            <span
              className="relative flex h-2 w-2"
              aria-label={activeShifts.length > 0 ? "live" : "idle"}
              title={activeShifts.length > 0 ? "Live" : "No active shifts"}
            >
              {activeShifts.length > 0 && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              )}
              <span
                className={`relative inline-flex h-2 w-2 rounded-full ${
                  activeShifts.length > 0 ? "bg-emerald-500" : "bg-slate-300"
                }`}
              />
            </span>
          </div>
          <span className="label-eyebrow" data-testid="active-shifts-count">
            {activeShifts.length} on shift
          </span>
        </div>

        {activeShifts.length === 0 ? (
          <p className="text-sm text-clinical-subtext italic" data-testid="active-shifts-empty">
            No caregivers currently clocked in.
          </p>
        ) : (
          <ul className="divide-y divide-clinical-border" data-testid="active-shifts-list">
            {activeShifts.map((s) => {
              const started = s.clock_in_at ? new Date(s.clock_in_at) : null;
              const mins = started
                ? Math.max(0, Math.floor((Date.now() - started.getTime()) / 60000))
                : 0;
              const dur = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
              return (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-3 py-2.5"
                  data-testid={`active-shift-${s.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-clinical-text truncate">
                      {s.caregiver_name}
                      <span className="font-normal text-clinical-subtext"> · {s.client_name}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-[11px] text-clinical-subtext">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {started ? started.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Timer className="h-3 w-3" /> {dur}
                      </span>
                      {(s.clock_in_lat != null && s.clock_in_lng != null) && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> geo-verified
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-surface p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-medium">Last 7 days</h2>
            <span className="label-eyebrow">Reports submitted</span>
          </div>
          <div className="h-56 min-h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.trend} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#004AE6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#004AE6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#4B5563"
                  fontSize={11}
                  tickFormatter={(d) => d.slice(5)}
                />
                <YAxis stroke="#4B5563" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#004AE6"
                  fill="url(#g1)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-medium">Alerts</h2>
            <Link
              to="/admin/alerts"
              className="text-xs text-clinical-primary hover:underline inline-flex items-center gap-1"
              data-testid="alerts-link"
            >
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {alerts.length === 0 ? (
            <div className="py-8 text-center text-sm text-clinical-subtext">
              All scheduled visits documented. Nice.
            </div>
          ) : (
            <ul className="space-y-2">
              {alerts.slice(0, 5).map((a, i) => (
                <li
                  key={`${a.client_id || a.kind || "alert"}-${a.date || ""}-${i}`}
                  className="flex items-start gap-3 rounded-lg border border-clinical-border p-3"
                  data-testid={`alert-item-${i}`}
                >
                  <span className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg ${
                    a.severity === "high"
                      ? "bg-destructive/10 text-destructive"
                      : "bg-amber-500/10 text-amber-600"
                  }`}>
                    <AlertTriangle className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.client_name}</div>
                    <div className="text-xs text-clinical-subtext">
                      Missed on {a.missed_date} ({a.day})
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-medium flex items-center gap-2">
            <Pill className="h-4 w-4 text-clinical-primary" />
            Recent medication changes
          </h2>
          <span className="label-eyebrow">Last 24 hours</span>
        </div>
        {medChanges.length === 0 ? (
          <div className="py-8 text-center text-sm text-clinical-subtext">
            No medication add/edit/delete in the last 24 hours.
          </div>
        ) : (
          <ul className="divide-y divide-clinical-border" data-testid="med-changes-list">
            {medChanges.slice(0, 8).map((c) => {
              const Icon =
                c.action === "create" ? Plus : c.action === "delete" ? Trash2 : Pencil;
              const iconClass =
                c.action === "create"
                  ? "bg-emerald-500/10 text-emerald-600"
                  : c.action === "delete"
                  ? "bg-destructive/10 text-destructive"
                  : "bg-clinical-accent/10 text-clinical-accent";
              return (
                <li
                  key={c.id}
                  className="py-3 flex items-start gap-3"
                  data-testid={`med-change-${c.id}`}
                >
                  <span className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg ${iconClass}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-clinical-text truncate">
                      {c.summary || `${c.action} medication`}
                    </div>
                    <div className="text-xs text-clinical-subtext">
                      {c.client_name && <>for {c.client_name} · </>}
                      by {c.actor_name}
                      <span className="ml-1 pill bg-clinical-bg text-clinical-subtext capitalize">
                        {c.actor_role}
                      </span>
                      <span className="ml-1.5">
                        {new Date(c.created_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-medium">Recent reports</h2>
          <Link
            to="/admin/reports"
            className="text-xs text-clinical-primary hover:underline inline-flex items-center gap-1"
            data-testid="recent-reports-link"
          >
            All reports <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="py-8 text-center text-sm text-clinical-subtext">
            No reports yet. Caregivers will appear here as they submit.
          </div>
        ) : (
          <ul className="divide-y divide-clinical-border">
            {recent.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-3" data-testid={`recent-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {r.client_name}
                  </div>
                  <div className="text-xs text-clinical-subtext">
                    {r.caregiver_name} · {r.shift_date} · <span className="capitalize">{r.shift_type}</span>
                  </div>
                </div>
                {r.incident_flag && (
                  <span className="pill bg-destructive/10 text-destructive">
                    Incident
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
